import React from 'react'
import moment from 'moment'
import Calendar from '@/components/calendar/Calendar'
import {
  IExistingScaleInterval,
  ICellConfig,
  ECellColor,
  getTimeslotColor,
  EBadgeType,
} from '@/components/calendar/calendarTypes'
import utils from '@/components/calendar/utils'
import { IFullSchema } from '@/store/apis/schemas'
import {
  useLazyAddTimeslotsQuery,
  useLazyUpdateTimeslotQuery,
  useLazyDeleteTimeslotsQuery,
  ETimeslotIdTypes,
} from '@/store/apis/timeslots'
import { IValue } from '@/components/dropDown/DropDown'

interface ISceneGroupsSchemaProps {
  isLoading: boolean
  firstDay: moment.Moment
  lastDay: moment.Moment
  schemaData: IFullSchema
  selectedSceneGroupId: number
  placeDropDownData: IValue[]
  onGetData(): void
  setFirstDay(newFirstDay: moment.Moment): void
}

const SceneGroups: React.FC<ISceneGroupsSchemaProps> = ({
  isLoading,
  firstDay,
  lastDay,
  schemaData,
  selectedSceneGroupId,
  placeDropDownData,
  onGetData,
  setFirstDay,
}) => {
  const [addTimeslotsQuery] = useLazyAddTimeslotsQuery()
  const [updatTimeslotQuery] = useLazyUpdateTimeslotQuery()
  const [deleteTimeslotsQuery] = useLazyDeleteTimeslotsQuery()
  const [timeslots, setTimeslots] = React.useState<IExistingScaleInterval[]>([])
  const [underlyingCellConfigs, setUnderlyingCellConfigs] = React.useState<
    ICellConfig[]
  >([])

  const sceneGroupData = schemaData.sceneGroups.find(
    (scene) => scene.sceneGroup.id === selectedSceneGroupId,
  )

  React.useEffect(() => {
    const existingCells: ICellConfig[] = []
    schemaData.schema.timeslots.map((timeslot) => {
      const isInsideInterval = moment(timeslot.startTimestamp).isBetween(
        firstDay,
        lastDay,
      )
      if (isInsideInterval) {
        const intervals = utils.getIntervalsByDuration(
          30,
          moment(timeslot.startTimestamp),
          moment(timeslot.endTimestamp),
        )
        intervals.map((interval) => {
          existingCells.push({
            startTimestamp: interval.start,
            color: getTimeslotColor(timeslot),
            badge: timeslot.comment && {
              type: EBadgeType.Scene,
              contents: [
                {
                  label: timeslot.comment,
                  comment: '',
                },
              ],
            },
          })
        })
      }
    })

    // Users
    const alreadyAddedUsers: number[] = []
    sceneGroupData?.scenes?.map((sceneGroupScene) => {
      const scene = schemaData.scenes.find(
        (schemaScene) => schemaScene.scene.id === sceneGroupScene.id,
      )
      const users = scene.users.map((user) => {
        const foundIndex = schemaData.users.findIndex(
          (schemaUser) => schemaUser.id === user.id,
        )
        if (foundIndex !== -1) {
          return schemaData.users[foundIndex]
        }
      })
      users.map((sceneUser) => {
        if (!alreadyAddedUsers.includes(sceneUser.id)) {
          const user = schemaData.users.find(
            (schemaUser) => schemaUser.id === sceneUser.id,
          )
          user.timeslots.map((timeslot) => {
            const isInsideInterval = moment(timeslot.startTimestamp).isBetween(
              firstDay,
              lastDay,
            )
            if (isInsideInterval) {
              const intervals = utils.getIntervalsByDuration(
                30,
                moment(timeslot.startTimestamp),
                moment(timeslot.endTimestamp),
              )
              intervals.map((interval) => {
                const founIndex = existingCells.findIndex(
                  (existing) =>
                    moment(existing.startTimestamp).isSame(
                      interval.start,
                      'minute',
                    ) && existing.color === ECellColor.User,
                )
                if (founIndex === -1) {
                  existingCells.push({
                    startTimestamp: interval.start,
                    color: ECellColor.User,
                    badge: {
                      type: EBadgeType.User,
                      contents: [
                        {
                          label: `${user.firstName} ${user.lastName}`,
                          comment: timeslot.comment,
                        },
                      ],
                    },
                  })
                } else {
                  const { badge } = existingCells[founIndex]
                  const badgeContents = [
                    {
                      label: `${user.firstName} ${user.lastName}`,
                      comment: timeslot.comment,
                    },
                  ]
                  existingCells[founIndex] = {
                    ...existingCells[founIndex],
                    badge: {
                      type: EBadgeType.User,
                      contents: badge
                        ? badge.contents.concat(badgeContents)
                        : badgeContents,
                    },
                  }
                }
              })
            }
          })
          alreadyAddedUsers.push(user.id)
        }
      })
    })

    // Other scene groups timeslots
    const existing: IExistingScaleInterval[] = []
    schemaData.sceneGroups.map((sceneGroup) => {
      const isCurrentScene = sceneGroup.sceneGroup.id === selectedSceneGroupId
      if (isCurrentScene) {
        sceneGroup.timeslots.map((timeslot) => {
          const isInsideInterval = moment(timeslot.startTimestamp).isBetween(
            firstDay,
            lastDay,
          )
          if (isInsideInterval) {
            existing.push({
              id: timeslot.id,
              start: moment(timeslot.startTimestamp),
              end: moment(timeslot.endTimestamp),
              color: ECellColor.Scene,
              comment: timeslot.comment,
              placeId: timeslot.placeId,
            })
          }
        })
      } else {
        sceneGroup.timeslots.map((timeslot) => {
          const isInsideInterval = moment(timeslot.startTimestamp).isBetween(
            firstDay,
            lastDay,
          )
          if (isInsideInterval) {
            const intervals = utils.getIntervalsByDuration(
              30,
              moment(timeslot.startTimestamp),
              moment(timeslot.endTimestamp),
            )
            intervals.map((interval, index) => {
              const founIndex = existingCells.findIndex(
                (existing) =>
                  moment(existing.startTimestamp).isSame(
                    interval.start,
                    'minute',
                  ) && existing.color === ECellColor.OtherScene,
              )
              if (founIndex === -1) {
                existingCells.push({
                  isStart: index === 0,
                  isEnd: index === intervals.length - 1,
                  startTimestamp: interval.start,
                  color: ECellColor.OtherScene,
                  badge: {
                    type: EBadgeType.Scene,
                    contents: [
                      {
                        label: sceneGroup.sceneGroup.name,
                        comment: timeslot.comment,
                      },
                    ],
                  },
                })
              } else {
                const { badge } = existingCells[founIndex]
                existingCells[founIndex] = {
                  ...existingCells[founIndex],
                  badge: {
                    type: EBadgeType.Scene,
                    contents: badge.contents.concat([
                      {
                        label: sceneGroup.sceneGroup.name,
                        comment: timeslot.comment,
                      },
                    ]),
                  },
                }
              }
            })
          }
        })
      }
    })

    // Scenes
    schemaData.scenes.map((scene) =>
      scene.timeslots.map((timeslot) => {
        const isInsideInterval = moment(timeslot.startTimestamp).isBetween(
          firstDay,
          lastDay,
        )
        if (isInsideInterval) {
          const intervals = utils.getIntervalsByDuration(
            30,
            moment(timeslot.startTimestamp),
            moment(timeslot.endTimestamp),
          )
          intervals.map((interval, index) => {
            const founIndex = existingCells.findIndex(
              (existing) =>
                moment(existing.startTimestamp).isSame(
                  interval.start,
                  'minute',
                ) && existing.color === ECellColor.OtherScene,
            )
            if (founIndex === -1) {
              existingCells.push({
                isStart: index === 0,
                isEnd: index === intervals.length - 1,
                startTimestamp: interval.start,
                color: ECellColor.OtherScene,
                badge: {
                  type: EBadgeType.Scene,
                  contents: [
                    {
                      label: scene.scene.name,
                      comment: timeslot.comment,
                    },
                  ],
                },
              })
            } else {
              const { badge } = existingCells[founIndex]
              existingCells[founIndex] = {
                ...existingCells[founIndex],
                badge: {
                  type: EBadgeType.Scene,
                  contents: badge.contents.concat([
                    {
                      label: scene.scene.name,
                      comment: timeslot.comment,
                    },
                  ]),
                },
              }
            }
          })
        }
      }),
    )
    setUnderlyingCellConfigs(existingCells)
    setTimeslots(existing)
  }, [schemaData, sceneGroupData, selectedSceneGroupId, firstDay, lastDay])

  const handleAddTimeslot = async (
    startTimestamp: Date,
    endTimestamp: Date,
    comment: string,
    placeId: number,
  ): Promise<void> => {
    await addTimeslotsQuery({
      parentType: ETimeslotIdTypes.SceneGroup,
      parentId: sceneGroupData.sceneGroup.id,
      schemaId: schemaData.schema.schema.id,
      timeslots: [
        {
          startTimestamp: startTimestamp.toISOString(),
          endTimestamp: endTimestamp.toISOString(),
          comment,
          placeId,
        },
      ],
    })
    onGetData()
  }

  const handleUpdateTimeslot = async (
    id: number,
    startTimestamp: Date,
    endTimestamp: Date,
    comment: string,
    placeId: number,
  ): Promise<void> => {
    await updatTimeslotQuery({
      id,
      parentType: ETimeslotIdTypes.SceneGroup,
      parentId: sceneGroupData.sceneGroup.id,
      timeslot: {
        startTimestamp: startTimestamp.toISOString(),
        endTimestamp: endTimestamp.toISOString(),
        comment,
        placeId,
      },
    })
    onGetData()
  }

  const handleDeleteTimeslots = async (ids: number[]): Promise<void> => {
    await deleteTimeslotsQuery({
      schemaId: schemaData.schema.schema.id,
      ids,
    })
    onGetData()
  }

  return (
    <Calendar
      isLoading={isLoading}
      firstDay={firstDay ?? moment()}
      onFirstDayUpdate={(newFirstDay) => setFirstDay(newFirstDay)}
      minDay={
        schemaData ? moment(schemaData.schema.schema.startDate) : undefined
      }
      maxDay={schemaData ? moment(schemaData.schema.schema.endDate) : undefined}
      createColor={ECellColor.Scene}
      selectedIntervals={timeslots}
      allowOverlap
      showPlaceOnEvent
      underlyingCellConfigs={underlyingCellConfigs}
      placeData={placeDropDownData}
      onIntervalSelect={handleAddTimeslot}
      onIntervalUpdate={handleUpdateTimeslot}
      onIntervalsRemove={handleDeleteTimeslots}
      calendarDayComment={
        schemaData
          ? schemaData.schema.days.map((day) => ({
              date: new Date(day.date),
              comment: day.comment,
            }))
          : []
      }
    />
  )
}

export default SceneGroups
